import React, { useState } from 'react';
import { Button, Modal, Text } from '@companycam/slab-web';
import styled from 'styled-components';
import { translate } from 'javascripts/translations/i18n';
import { useModal } from 'components/shared/hooks';
import { useCreateReviewProfile } from 'data/v3/reviewProfiles';
import { useReviewProfiles } from 'components/reviews/hooks/useReviewProfiles';
import ReviewProfileSearch from 'components/reviews/ReviewProfileSearch';
import toast from 'helpers/toast';
import { type GoogleReviewProfileData } from 'components/reviews/types';

export type CreateReviewProfileModalProps = {
  onSuccess?: () => void;
};

const StyledModal = styled(Modal)`
  padding: 0;
  margin: 0;
`;

const cancelText = translate('common.cancel');
const connectText = translate('common.connect');

export const CreateReviewProfileModal = ({
  onSuccess,
}: CreateReviewProfileModalProps) => {
  const { hideModal } = useModal();
  const { isReviewProfilesLoading, refetchReviewProfiles } =
    useReviewProfiles();
  const [reviewProfileData, setReviewProfileData] =
    useState<GoogleReviewProfileData | null>(null);

  const handleSuccess = () => {
    refetchReviewProfiles();
    toast.success(translate('project.reviews.reviewProfile.googleLinkSuccess'));
    hideModal();
    onSuccess?.();
  };

  const handleError = () => {
    refetchReviewProfiles();
    hideModal();
    toast.error(translate('project.reviews.reviewProfile.googleLinkError'));
  };

  const reviewProfileMutation = useCreateReviewProfile({
    onError: handleError,
    onSuccess: handleSuccess,
  });

  const handleSubmit = () => {
    reviewProfileMutation.mutate({
      googlePlaceId: reviewProfileData.googlePlaceId,
      nickname: reviewProfileData.nickname,
    });
  };

  return (
    <StyledModal
      title={translate('project.reviews.reviewsHub.connectGoogleTitle')}
      headerComponent={null}
      size="small"
      isOpen
      onClose={hideModal}
      footerActions={
        <>
          <Button onClick={hideModal}>{cancelText}</Button>
          <Button
            color="primary"
            onClick={handleSubmit}
            disabled={!reviewProfileData}
            data-testid="reviews__add-business-modal-connect-button"
          >
            {connectText}
          </Button>
        </>
      }
    >
      <Text
        as="h2"
        size="xl"
        ccMargin="none none m none"
        data-testid="reviews__add-business-modal-header"
      >
        {translate('project.reviews.reviewsHub.connectGoogleBusiness')}
      </Text>
      {isReviewProfilesLoading ? null : (
        <ReviewProfileSearch
          setReviewProfileData={setReviewProfileData}
          reviewProfileData={reviewProfileData}
        />
      )}
    </StyledModal>
  );
};
